import { useState, useEffect, useMemo } from "react";

const C = {
  border: "var(--border)",
  text: "var(--text-main)",
  muted: "var(--text-muted)",
  accent: "var(--accent)",
  success: "var(--success)",
  danger: "var(--danger)",
};

const api = async (url, method = "GET", body = null) => {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : null,
  });
  return res.json();
};

export default function TabBridge() {
  const [accounts, setAccounts] = useState([]);
  const [signalSets, setSignalSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: true });

  // Form state — Signal Bridge
  const [selAccount, setSelAccount] = useState("");
  const [selSet, setSelSet] = useState("");

  // Account Bridge state
  const [accountBridges, setAccountBridges] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [abSource, setAbSource] = useState("");
  const [abDest, setAbDest] = useState("");
  const [abLot, setAbLot] = useState("1.0");
  const [abNoSlDampening, setAbNoSlDampening] = useState("0.5");
  const [abLotMode, setAbLotMode] = useState("AUTO");
  const [abFixedLot, setAbFixedLot] = useState("0.01");
  const [abSaving, setAbSaving] = useState(false);
  const [abMsg, setAbMsg] = useState({ text: "", ok: true });
  const [ssbSourceType, setSsbSourceType] = useState("account");
  const [ssbSignalSet, setSsbSignalSet] = useState("");
  const [ssbLotMode, setSsbLotMode] = useState("AUTO");
  const [ssbFixedLot, setSsbFixedLot] = useState("0.01");
  const [ssbRiskPercent, setSsbRiskPercent] = useState("1.0");
  const [signalSetBridges, setSignalSetBridges] = useState([]);
  const [editingAccId, setEditingAccId] = useState(null);
  const [editAccLotMode, setEditAccLotMode] = useState("AUTO");
  const [editAccFixedLot, setEditAccFixedLot] = useState("0.01");
  const [editAccNoSlDamp, setEditAccNoSlDamp] = useState("0.5");
  const [editingSsbId, setEditingSsbId] = useState(null);
  const [editSsbLotMode2, setEditSsbLotMode2] = useState("AUTO");
  const [editSsbFixedLot2, setEditSsbFixedLot2] = useState("0.01");
  const [editSsbRiskPercent2, setEditSsbRiskPercent2] = useState("1.0");
  // Create dest account state
  const [users, setUsers] = useState([]);
  const [showCreateDest, setShowCreateDest] = useState(false);
  const [destForm, setDestForm] = useState({ account_number: "", platform: "MT5", broker: "", server: "", nickname: "", is_cent: false, owner_user_id: "" });
  const [destSaving, setDestSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [br, ss, ab, aa, ul, ada, ssb] = await Promise.all([
        api("/api/admin/signal-bridges"),
        api("/api/admin/signal-sets-list"),
        api("/api/admin/copy-bridges"),
        api("/api/admin/all-accounts"),
        api("/api/admin/users-list"),
        api("/api/admin/admin-accounts"),
        api("/api/admin/signal-set-bridges"),
      ]);
      setAccounts(br.data || []);
      setSignalSets(ss.data || []);
      setAccountBridges(ab.data || []);
      setAllAccounts(aa.data || []);
      setUsers(ul.data || []);
      setAdminAccounts(ada.data || []);
      setSignalSetBridges(ssb.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Hanya akun yang belum di-bridge untuk dropdown Add
  const unbridged = useMemo(() =>
    (accounts || []).filter(a => !a.auto_sync_signals || !a.linked_signal_set_id),
    [accounts]);

  // Akun yang sudah aktif bridge
  const bridged = useMemo(() =>
    (accounts || []).filter(a => a.auto_sync_signals && a.linked_signal_set_id),
    [accounts]);

  const displayEquity = (a) => {
    const val = a.is_cent ? (a.equity / 100) : a.equity;
    return "$" + (val || 0).toFixed(2) + (a.is_cent ? " ¢" : "");
  };

  const handleAdd = async () => {
    if (!selAccount || !selSet) return;
    setSaving(true);
    try {
      await api(`/api/admin/signal-bridges/${selAccount}`, "PUT", {
        auto_sync_signals: true,
        linked_signal_set_id: selSet,
      });
      setMsg({ text: "Bridge aktif ", ok: true });
      setSelAccount("");
      setSelSet("");
      await loadData();
    } catch (e) {
      setMsg({ text: "Error", ok: false });
    }
    setSaving(false);
    setTimeout(() => setMsg({ text: "", ok: true }), 3000);
  };

  const handleRemove = async (acct) => {
    if (!confirm(`Hapus bridge untuk akun ${acct.account_number}?`)) return;
    try {
      await api(`/api/admin/signal-bridges/${acct.account_id}`, "PUT", {
        auto_sync_signals: false,
        linked_signal_set_id: "",
      });
      setMsg({ text: "Bridge dinonaktifkan", ok: true });
      await loadData();
    } catch (e) {
      setMsg({ text: "Error", ok: false });
    }
    setTimeout(() => setMsg({ text: "", ok: true }), 3000);
  };

  const handleToggleActive = async (acct) => {
    try {
      await api(`/api/admin/signal-bridges/${acct.account_id}`, "PUT", {
        auto_sync_signals: !acct.auto_sync_signals,
        linked_signal_set_id: acct.linked_signal_set_id,
      });
      await loadData();
    } catch (e) {
      setMsg({ text: "Error", ok: false });
    }
  };

  // Create Dest Account handler
  const handleCreateDest = async () => {
    if (!destForm.account_number || !destForm.nickname || !destForm.owner_user_id) return;
    setDestSaving(true);
    try {
      const res = await api("/api/admin/account-bridges/create-dest", "POST", destForm);
      if (res.ok) {
        setAbMsg({ text: "Dest account created: " + res.account_id, ok: true });
        setShowCreateDest(false);
        setDestForm({ account_number: "", platform: "MT5", broker: "", server: "", nickname: "", is_cent: false, owner_user_id: "" });
        await loadData();
      } else {
        setAbMsg({ text: res.error || "Error creating account", ok: false });
      }
    } catch (e) {
      setAbMsg({ text: "Error", ok: false });
    }
    setDestSaving(false);
    setTimeout(() => setAbMsg({ text: "", ok: true }), 4000);
  };

  // Account Bridge handlers
  const handleAddAccountBridge = async () => {
    if (ssbSourceType === "signal") {
      if (!ssbSignalSet || !abDest) return;
      setAbSaving(true);
      try {
        const res = await api("/api/admin/signal-set-bridges", "POST", {
          set_id: ssbSignalSet,
          dest_account_id: abDest,
          bridge_lot_mode: ssbLotMode,
          bridge_fixed_lot: parseFloat(ssbFixedLot) || 0.01,
          bridge_risk_percent: parseFloat(ssbRiskPercent) || 1.0,
        });
        if (res.ok) {
          setAbMsg({ text: "Signal Set Bridge created", ok: true });
          setSsbSignalSet(""); setAbDest(""); setSsbLotMode("AUTO"); setSsbFixedLot("0.01"); setSsbRiskPercent("1.0");
          await loadData();
        } else {
          setAbMsg({ text: res.error || "Error", ok: false });
        }
      } catch (e) {
        setAbMsg({ text: "Error", ok: false });
      }
      setAbSaving(false);
      setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
      return;
    }
    if (!abSource || !abDest) return;
    setAbSaving(true);
    try {
      const res = await api("/api/admin/copy-bridges", "POST", {
        source_account_id: abSource,
        dest_account_id: abDest,
        lot_multiplier: parseFloat(abLot) || 1.0,
        no_sl_dampening_factor: parseFloat(abNoSlDampening) || 0.5,
        bridge_lot_mode: abLotMode,
        bridge_fixed_lot: parseFloat(abFixedLot) || 0.01,
      });
      if (res.ok) {
        setAbMsg({ text: "Bridge created", ok: true });
        setAbSource(""); setAbDest(""); setAbLot("1.0"); setAbNoSlDampening("0.5");
        setAbLotMode("AUTO"); setAbFixedLot("0.01");
        await loadData();
      } else {
        setAbMsg({ text: res.error || "Error", ok: false });
      }
    } catch (e) {
      setAbMsg({ text: "Error", ok: false });
    }
    setAbSaving(false);
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  const handleToggleAccountBridge = async (b) => {
    const newStatus = b.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    await api(`/api/admin/copy-bridges/${b.id}`, "PUT", {
      status: newStatus,
    });
    await loadData();
  };

  const handleDeleteAccountBridge = async (id) => {
    if (!confirm("Delete this account bridge?")) return;
    await api(`/api/admin/copy-bridges/${id}`, "DELETE");
    setAbMsg({ text: "Bridge deleted", ok: true });
    await loadData();
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  const handleHardDeleteAccountBridge = async (id) => {
    if (!confirm("Hapus permanen bridge ini? Tindakan ini tidak bisa dibatalkan.")) return;
    const res = await api(`/api/admin/copy-bridges/${id}/permanent`, "DELETE");
    if (res.ok) {
      setAbMsg({ text: "Bridge permanently deleted", ok: true });
    } else {
      setAbMsg({ text: res.error || "Error", ok: false });
    }
    await loadData();
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  const handleToggleSignalSetBridge = async (b) => {
    const newStatus = b.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    await api(`/api/admin/signal-set-bridges/${b.id}`, "PUT", {
      status: newStatus,
    });
    await loadData();
  };

  const handleDeleteSignalSetBridge = async (id) => {
    if (!confirm("Delete this signal set bridge?")) return;
    await api(`/api/admin/signal-set-bridges/${id}`, "DELETE");
    setAbMsg({ text: "Bridge deleted", ok: true });
    await loadData();
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  const handleHardDeleteSignalSetBridge = async (id) => {
    if (!confirm("Hapus permanen bridge ini? Tindakan ini tidak bisa dibatalkan.")) return;
    const res = await api(`/api/admin/signal-set-bridges/${id}/permanent`, "DELETE");
    if (res.ok) {
      setAbMsg({ text: "Bridge permanently deleted", ok: true });
    } else {
      setAbMsg({ text: res.error || "Error", ok: false });
    }
    await loadData();
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  // Edit handlers — Account Bridge
  const startEditAccountBridge = (b) => {
    setEditingAccId(b.id);
    setEditAccLotMode(b.bridge_lot_mode || "AUTO");
    setEditAccFixedLot(b.bridge_fixed_lot != null ? String(b.bridge_fixed_lot) : "0.01");
    setEditAccNoSlDamp(b.no_sl_dampening_factor != null ? String(b.no_sl_dampening_factor) : "0.5");
  };
  const cancelEditAccountBridge = () => setEditingAccId(null);
  const saveEditAccountBridge = async (id) => {
    const res = await api(`/api/admin/copy-bridges/${id}/config`, "PUT", {
      bridge_lot_mode: editAccLotMode,
      bridge_fixed_lot: parseFloat(editAccFixedLot) || 0.01,
      no_sl_dampening_factor: parseFloat(editAccNoSlDamp) || 0.5,
    });
    if (res.ok) {
      setAbMsg({ text: "Bridge config updated", ok: true });
      setEditingAccId(null);
      await loadData();
    } else {
      setAbMsg({ text: res.error || "Error", ok: false });
    }
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  // Edit handlers — Signal Set Bridge
  const startEditSignalSetBridge = (b) => {
    setEditingSsbId(b.id);
    setEditSsbLotMode2(b.bridge_lot_mode || "AUTO");
    setEditSsbFixedLot2(b.bridge_fixed_lot != null ? String(b.bridge_fixed_lot) : "0.01");
    setEditSsbRiskPercent2(b.bridge_risk_percent != null ? String(b.bridge_risk_percent) : "1.0");
  };
  const cancelEditSignalSetBridge = () => setEditingSsbId(null);
  const saveEditSignalSetBridge = async (id) => {
    const res = await api(`/api/admin/signal-set-bridges/${id}/config`, "PUT", {
      bridge_lot_mode: editSsbLotMode2,
      bridge_fixed_lot: parseFloat(editSsbFixedLot2) || 0.01,
      bridge_risk_percent: parseFloat(editSsbRiskPercent2) || 1.0,
    });
    if (res.ok) {
      setAbMsg({ text: "Bridge config updated", ok: true });
      setEditingSsbId(null);
      await loadData();
    } else {
      setAbMsg({ text: res.error || "Error", ok: false });
    }
    setTimeout(() => setAbMsg({ text: "", ok: true }), 3000);
  };

  if (loading) return <div style={{ padding: 40, color: C.muted, fontSize: 13 }}>Loading...</div>;

  return (
    <div style={{ padding: "0 0 40px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>Bridge Config</h2>
          <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>Signal Bridge: trader → analyst signal set &nbsp;|&nbsp; Account Bridge: any account → trader account</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {msg.text && <span style={{ fontSize: 12, color: msg.ok ? C.success : C.danger }}>{msg.text}</span>}
          <button onClick={loadData} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, cursor: "pointer" }}>Refresh</button>
        </div>
      </div>

      {/* Form tambah bridge */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 32, padding: 16, borderRadius: 10, border: `1px solid ${C.border}`, background: "var(--bg-card)" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>TRADER ACCOUNT</div>
          <select
            value={selAccount}
            onChange={e => setSelAccount(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: selAccount ? C.text : C.muted, cursor: "pointer" }}
          >
            <option value="">— Pilih akun —</option>
            {unbridged.map(a => (
              <option key={a.account_id} value={a.account_id}>
                {a.account_number} · {a.platform} · {a.user_name} ({a.user_email})
              </option>
            ))}
          </select>
        </div>

        <div style={{ fontSize: 18, color: C.muted, paddingTop: 20 }}>→</div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>SIGNAL SET</div>
          <select
            value={selSet}
            onChange={e => setSelSet(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: selSet ? C.text : C.muted, cursor: "pointer" }}
          >
            <option value="">— Pilih signal set —</option>
            {signalSets.map(ss => (
              <option key={ss.id} value={ss.id}>{ss.name} ({ss.analyst_email})</option>
            ))}
          </select>
        </div>

        <div style={{ paddingTop: 20 }}>
          <button
            onClick={handleAdd}
            disabled={!selAccount || !selSet || saving}
            style={{
              padding: "8px 20px", fontSize: 13, borderRadius: 7, border: "none",
              background: selAccount && selSet ? C.accent : C.border,
              color: selAccount && selSet ? "#fff" : C.muted,
              cursor: selAccount && selSet ? "pointer" : "not-allowed",
              fontWeight: 700,
            }}
          >
            {saving ? "..." : "Add Bridge"}
          </button>
        </div>
      </div>

      {/* List bridge aktif */}
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
        Active Bridges ({bridged.length})
      </div>

      {bridged.length === 0 && (
        <div style={{ padding: "40px 0", textAlign: "center", color: C.muted, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          Belum ada bridge aktif — tambah di atas
        </div>
      )}

      {bridged.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Trader Account", "Platform", "Equity", "→ Signal Set", "Status", ""].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 600, fontSize: 11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bridged.map(a => (
              <tr key={a.account_id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: "12px 10px" }}>
                  <div style={{ fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{a.account_number}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{a.user_name} · {a.user_email}</div>
                </td>
                <td style={{ padding: "12px 10px", color: C.muted }}>{a.platform} · {a.broker}</td>
                <td style={{ padding: "12px 10px", color: C.text }}>{displayEquity(a)}</td>
                <td style={{ padding: "12px 10px", color: C.accent, fontWeight: 600 }}>{a.linked_set_name || a.linked_signal_set_id}</td>
                <td style={{ padding: "12px 10px" }}>
                  <button
                    onClick={() => handleToggleActive(a)}
                    style={{
                      padding: "4px 14px", fontSize: 12, borderRadius: 20, cursor: "pointer", fontWeight: 700, border: "none",
                      background: a.auto_sync_signals ? C.success + "22" : C.border,
                      color: a.auto_sync_signals ? C.success : C.muted,
                    }}
                  >
                    {a.auto_sync_signals ? "ON" : "OFF"}
                  </button>
                </td>
                <td style={{ padding: "12px 10px" }}>
                  <button
                    onClick={() => handleRemove(a)}
                    style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.danger}22`, background: C.danger + "11", color: C.danger, fontWeight: 600 }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── ACCOUNT BRIDGE ─────────────────────────────────── */}
      <div style={{ marginTop: 48 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>Account Bridge</h2>
            <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>Curated copy — dest eksekusi REAL di MT4/5-nya sendiri. Source TIDAK tahu, TIDAK ada fee. Dest wajib akun admin.</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {abMsg.text && <span style={{ fontSize: 12, color: abMsg.ok ? C.success : C.danger }}>{abMsg.text}</span>}
            <button onClick={() => setShowCreateDest(v => !v)}
              style={{ fontSize: 12, padding: "6px 14px", borderRadius: 6, border: `1px solid ${C.accent}`, background: showCreateDest ? C.accent : "transparent", color: showCreateDest ? "#fff" : C.accent, cursor: "pointer", fontWeight: 600 }}>
              {showCreateDest ? "✕ Cancel" : "+ Create Dest Account"}
            </button>
          </div>
        </div>

        {/* Form Create Dest Account */}
        {showCreateDest && (
          <div style={{ padding: 16, borderRadius: 10, border: `1px solid ${C.accent}33`, background: "var(--bg-elevated)", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>Create Dest Trader Account</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>ACCOUNT NUMBER</div>
                <input value={destForm.account_number} onChange={e => setDestForm(f => ({...f, account_number: e.target.value}))}
                  placeholder="e.g. 12345678"
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>PLATFORM</div>
                <select value={destForm.platform} onChange={e => setDestForm(f => ({...f, platform: e.target.value}))}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}>
                  <option value="MT5">MT5</option>
                  <option value="MT4">MT4</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>BROKER</div>
                <input value={destForm.broker} onChange={e => setDestForm(f => ({...f, broker: e.target.value}))}
                  placeholder="e.g. Markets4You"
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>SERVER</div>
                <input value={destForm.server} onChange={e => setDestForm(f => ({...f, server: e.target.value}))}
                  placeholder="e.g. Markets4You-Live3"
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>NICKNAME</div>
                <input value={destForm.nickname} onChange={e => setDestForm(f => ({...f, nickname: e.target.value}))}
                  placeholder="Display name di marketplace"
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text, boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>OWNER USER</div>
                <select value={destForm.owner_user_id} onChange={e => setDestForm(f => ({...f, owner_user_id: e.target.value}))}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: destForm.owner_user_id ? C.text : C.muted }}>
                  <option value="">— Pilih owner —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text, cursor: "pointer", paddingBottom: 8 }}>
                  <input type="checkbox" checked={destForm.is_cent} onChange={e => setDestForm(f => ({...f, is_cent: e.target.checked}))} />
                  Cent Account
                </label>
                <button onClick={handleCreateDest} disabled={!destForm.account_number || !destForm.nickname || !destForm.owner_user_id || destSaving}
                  style={{ padding: "8px 20px", fontSize: 13, borderRadius: 7, border: "none", fontWeight: 700, cursor: "pointer",
                    background: destForm.account_number && destForm.nickname && destForm.owner_user_id ? C.accent : C.border,
                    color: destForm.account_number && destForm.nickname && destForm.owner_user_id ? "#fff" : C.muted }}>
                  {destSaving ? "Creating..." : "Create Account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form tambah Account Bridge */}
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 28, padding: 16, borderRadius: 10, border: `1px solid ${C.border}`, background: "var(--bg-card)" }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>SOURCE TYPE</div>
            <select value={ssbSourceType} onChange={e => { setSsbSourceType(e.target.value); setAbSource(""); setSsbSignalSet(""); }}
              style={{ width: 130, padding: "8px 10px", fontSize: 12, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}>
              <option value="account">Account</option>
              <option value="signal">Signal Set</option>
            </select>
          </div>
          <div style={{ flex: 1 }}>
            {ssbSourceType === "account" ? (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>SOURCE ACCOUNT</div>
                <select value={abSource} onChange={e => setAbSource(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: abSource ? C.text : C.muted }}>
                  <option value="">— Pilih source —</option>
                  {allAccounts.map(a => (
                    <option key={a.id} value={a.id}>{a.account_number} · {a.platform} · {a.user_name} ({a.user_email})</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>SIGNAL SET SOURCE</div>
                <select value={ssbSignalSet} onChange={e => setSsbSignalSet(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: ssbSignalSet ? C.text : C.muted }}>
                  <option value="">— Pilih signal set —</option>
                  {signalSets.map(ss => (
                    <option key={ss.id} value={ss.id}>{ss.name} ({ss.analyst_email})</option>
                  ))}
                </select>
              </>
            )}
          </div>
          <div style={{ fontSize: 18, color: C.muted, paddingBottom: 8 }}>→</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>DEST TRADER ACCOUNT</div>
            <select value={abDest} onChange={e => setAbDest(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: abDest ? C.text : C.muted }}>
              <option value="">— Pilih dest —</option>
              {adminAccounts.filter(a => a.id !== abSource).map(a => (
                <option key={a.id} value={a.id}>{a.account_number} · {a.platform} · {a.user_name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>LOT SIZING</div>
            <select
              value={ssbSourceType === "account" ? abLotMode : ssbLotMode}
              onChange={e => ssbSourceType === "account" ? setAbLotMode(e.target.value) : setSsbLotMode(e.target.value)}
              style={{ width: 140, padding: "8px 10px", fontSize: 12, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}>
              <option value="AUTO">Auto (equity-based)</option>
              <option value="FIXED">Fixed lot</option>
            </select>
          </div>
          {(ssbSourceType === "account" ? abLotMode : ssbLotMode) === "FIXED" && (
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>FIXED LOT</div>
              <input
                type="number" min="0.01" step="0.01"
                value={ssbSourceType === "account" ? abFixedLot : ssbFixedLot}
                onChange={e => ssbSourceType === "account" ? setAbFixedLot(e.target.value) : setSsbFixedLot(e.target.value)}
                placeholder="0.01"
                style={{ width: 90, padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}
              />
            </div>
          )}
          {ssbSourceType === "signal" && (
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>RISK %</div>
              <input
                type="number" min="0.1" max="100" step="0.1"
                value={ssbRiskPercent}
                onChange={e => setSsbRiskPercent(e.target.value)}
                placeholder="1.0"
                style={{ width: 90, padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}
              />
            </div>
          )}
          {ssbSourceType === "account" && (
            <div>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>NO-SL DAMPENING</div>
              <input
                type="number" min="0.1" max="1.0" step="0.1"
                value={abNoSlDampening}
                onChange={e => setAbNoSlDampening(e.target.value)}
                style={{ width: 100, padding: "8px 10px", fontSize: 13, borderRadius: 7, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}
              />
            </div>
          )}
          <button onClick={handleAddAccountBridge}
            disabled={ssbSourceType === "signal" ? (!ssbSignalSet || !abDest || abSaving) : (!abSource || !abDest || abSaving)}
            style={{ padding: "8px 20px", fontSize: 13, borderRadius: 7, border: "none",
              background: (ssbSourceType === "signal" ? (ssbSignalSet && abDest) : (abSource && abDest)) ? C.accent : C.border,
              color: (ssbSourceType === "signal" ? (ssbSignalSet && abDest) : (abSource && abDest)) ? "#fff" : C.muted,
              cursor: (ssbSourceType === "signal" ? (ssbSignalSet && abDest) : (abSource && abDest)) ? "pointer" : "not-allowed", fontWeight: 700 }}>
            {abSaving ? "..." : "Add Bridge"}
          </button>
        </div>

        {/* List Account Bridges */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
          Active Account Bridges ({accountBridges.length})
        </div>
        {accountBridges.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: C.muted, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
            Belum ada account bridge — tambah di atas
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Source Account", "→", "Dest Trader Account", "Lot Mult", "No-SL Damp", "Status", ""].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 600, fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accountBridges.map(b => (
                <tr key={b.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 10px" }}>
                    <div style={{ fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{b.source_number}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{b.source_platform} · {b.source_user_name}</div>
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted }}>→</td>
                  <td style={{ padding: "12px 10px" }}>
                    <div style={{ fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{b.dest_number}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{b.dest_platform} · {b.dest_user_name}</div>
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted, fontSize: 12 }}>
                    {editingAccId === b.id ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <select value={editAccLotMode} onChange={e => setEditAccLotMode(e.target.value)}
                          style={{ padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}>
                          <option value="AUTO">Auto</option>
                          <option value="FIXED">Fixed</option>
                        </select>
                        {editAccLotMode === "FIXED" && (
                          <input type="number" min="0.01" step="0.01" value={editAccFixedLot} onChange={e => setEditAccFixedLot(e.target.value)}
                            style={{ width: 60, padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }} />
                        )}
                      </div>
                    ) : (
                      b.bridge_lot_mode === "FIXED" ? `Fixed ${b.bridge_fixed_lot != null ? b.bridge_fixed_lot.toFixed(2) : "0.01"}` : "Auto (equity)"
                    )}
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted, fontSize: 12 }}>
                    {editingAccId === b.id ? (
                      <input type="number" min="0.1" max="1.0" step="0.1" value={editAccNoSlDamp} onChange={e => setEditAccNoSlDamp(e.target.value)}
                        style={{ width: 60, padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }} />
                    ) : (
                      b.no_sl_dampening_factor != null ? b.no_sl_dampening_factor.toFixed(2) : "0.50"
                    )}
                  </td>
                  <td style={{ padding: "12px 10px" }}>
                    <button onClick={() => handleToggleAccountBridge(b)}
                      style={{ padding: "4px 14px", fontSize: 12, borderRadius: 20, cursor: "pointer", fontWeight: 700, border: "none",
                        background: b.status === "ACTIVE" ? C.success + "22" : C.border,
                        color: b.status === "ACTIVE" ? C.success : C.muted }}>
                      {b.status === "ACTIVE" ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td style={{ padding: "12px 10px", display: "flex", gap: 6 }}>
                    {editingAccId === b.id ? (
                      <>
                        <button onClick={() => saveEditAccountBridge(b.id)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: "none", background: C.accent, color: "#fff", fontWeight: 600 }}>
                          Save
                        </button>
                        <button onClick={cancelEditAccountBridge}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontWeight: 600 }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEditAccountBridge(b)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.accent}44`, background: C.accent + "11", color: C.accent, fontWeight: 600 }}>
                          Edit
                        </button>
                        <button onClick={() => handleDeleteAccountBridge(b.id)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.danger}22`, background: C.danger + "11", color: C.danger, fontWeight: 600 }}>
                          Delete
                        </button>
                        {b.status !== "ACTIVE" && (
                          <button onClick={() => handleHardDeleteAccountBridge(b.id)}
                            style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.danger}`, background: C.danger, color: "#fff", fontWeight: 600 }}>
                            Hapus Permanen
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* List Signal Set Bridges */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600, marginBottom: 10, marginTop: 28, textTransform: "uppercase", letterSpacing: 1 }}>
          Active Signal Set Bridges ({signalSetBridges.length})
        </div>
        {signalSetBridges.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: C.muted, fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
            Belum ada signal set bridge — pilih Source Type "Signal Set" di atas
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {["Signal Set", "→", "Dest Trader Account", "Lot Sizing", "Risk %", "Status", ""].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 600, fontSize: 11 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {signalSetBridges.map(b => (
                <tr key={b.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "12px 10px" }}>
                    <div style={{ fontWeight: 700, color: C.text }}>{b.set_name}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{b.analyst_name}</div>
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted }}>→</td>
                  <td style={{ padding: "12px 10px" }}>
                    <div style={{ fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{b.dest_number}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{b.dest_platform} · {b.dest_user_name}</div>
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted, fontSize: 12 }}>
                    {editingSsbId === b.id ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <select value={editSsbLotMode2} onChange={e => setEditSsbLotMode2(e.target.value)}
                          style={{ padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }}>
                          <option value="AUTO">Auto</option>
                          <option value="FIXED">Fixed</option>
                        </select>
                        {editSsbLotMode2 === "FIXED" && (
                          <input type="number" min="0.01" step="0.01" value={editSsbFixedLot2} onChange={e => setEditSsbFixedLot2(e.target.value)}
                            style={{ width: 60, padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }} />
                        )}
                      </div>
                    ) : (
                      b.bridge_lot_mode === "FIXED" ? `Fixed ${b.bridge_fixed_lot != null ? b.bridge_fixed_lot.toFixed(2) : "0.01"}` : "Auto (SL-based)"
                    )}
                  </td>
                  <td style={{ padding: "12px 10px", color: C.muted, fontSize: 12 }}>
                    {editingSsbId === b.id ? (
                      <input type="number" min="0.1" max="100" step="0.1" value={editSsbRiskPercent2} onChange={e => setEditSsbRiskPercent2(e.target.value)}
                        style={{ width: 60, padding: "4px 6px", fontSize: 11, borderRadius: 5, border: `1px solid ${C.border}`, background: "var(--bg-main)", color: C.text }} />
                    ) : (
                      <>{b.bridge_risk_percent != null ? b.bridge_risk_percent.toFixed(2) : "1.00"}%</>
                    )}
                  </td>
                  <td style={{ padding: "12px 10px" }}>
                    <button onClick={() => handleToggleSignalSetBridge(b)}
                      style={{ padding: "4px 14px", fontSize: 12, borderRadius: 20, cursor: "pointer", fontWeight: 700, border: "none",
                        background: b.status === "ACTIVE" ? C.success + "22" : C.border,
                        color: b.status === "ACTIVE" ? C.success : C.muted }}>
                      {b.status === "ACTIVE" ? "ON" : "OFF"}
                    </button>
                  </td>
                  <td style={{ padding: "12px 10px", display: "flex", gap: 6 }}>
                    {editingSsbId === b.id ? (
                      <>
                        <button onClick={() => saveEditSignalSetBridge(b.id)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: "none", background: C.accent, color: "#fff", fontWeight: 600 }}>
                          Save
                        </button>
                        <button onClick={cancelEditSignalSetBridge}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontWeight: 600 }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEditSignalSetBridge(b)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.accent}44`, background: C.accent + "11", color: C.accent, fontWeight: 600 }}>
                          Edit
                        </button>
                        <button onClick={() => handleDeleteSignalSetBridge(b.id)}
                          style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.danger}22`, background: C.danger + "11", color: C.danger, fontWeight: 600 }}>
                          Delete
                        </button>
                        {b.status !== "ACTIVE" && (
                          <button onClick={() => handleHardDeleteSignalSetBridge(b.id)}
                            style={{ padding: "4px 12px", fontSize: 12, borderRadius: 6, cursor: "pointer", border: `1px solid ${C.danger}`, background: C.danger, color: "#fff", fontWeight: 600 }}>
                            Hapus Permanen
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
